import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";

import { UiSingleSwitch } from "../../../../_uiComponents/telerikComponents/UiSwitch";
import {
     participantGetAreasRequest,
     retailSalesClearSaveDealersResult,
     retailSalesDealerListRequest,
     retailSalesSaveDealersRequest,
} from "../../../../../_redux/_actions";
import { retailSalesConstants } from "../../../../../_redux/_constants/retailSales.constants";

const ALL_OPTION = { id: null, name: "All" };

const RETAIL_OPTIONS = [
     { id: null, name: "All" },
     { id: true, name: "Retail" },
     { id: false, name: "Not retail" },
];

const INITIAL_PAGE = { skip: 0, take: 10 };

/**
 * Every Equipe dealer, with the retail sales flag as a switch. A dealer keeps whatever else it
 * already is (Aftersales, LCV); the retail flag sits on top and can be switched off again.
 */
const RetailDealersGrid = (props) => {
     const { onAddDealer } = props;

     const actionDispatch = useDispatch();

     const dealerPage = useSelector(
          (state) => state?.retailSales?.dealerListPage,
     );
     const areas = useSelector(
          (state) => state?.participantsManagement?.areaList,
     );
     const saveResult = useSelector(
          (state) => state?.retailSales?.saveDealersResult,
     );
     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               retailSalesConstants.RETAIL_SALES_DEALER_LIST_REQUEST,
          ),
     );

     const [search, setSearch] = useState("");
     const [retailFilter, setRetailFilter] = useState(RETAIL_OPTIONS[0]);
     const [areaFilter, setAreaFilter] = useState(ALL_OPTION);
     const [page, setPage] = useState(INITIAL_PAGE);
     const [sort, setSort] = useState([]);
     const [failureMessage, setFailureMessage] = useState("");

     // Dealers ticked on screen but not saved yet: the backend needs a retail area, so nothing is
     // sent until one is picked. Keyed by dealer id.
     const [pendingRetail, setPendingRetail] = useState({});

     useEffect(() => {
          actionDispatch(
               participantGetAreasRequest({
                    applicationId:
                         retailSalesConstants.RETAIL_SALES_APPLICATION_EQUIPE_ID,
               }),
          );
     }, [actionDispatch]);

     // Search, filters, sorting and paging are all applied by the backend.
     const loadDealers = useCallback(() => {
          actionDispatch(
               retailSalesDealerListRequest({
                    search: search.trim() ? search.trim() : undefined,
                    isRetailCarSalesDealer: retailFilter.id,
                    retailAreaId: areaFilter.id,
                    page: Math.floor(page.skip / page.take) + 1,
                    pageSize: page.take,
                    sortBy: sort.length ? sort[0].field : undefined,
                    sortDirection: sort.length ? sort[0].dir : undefined,
               }),
          );
     }, [actionDispatch, search, retailFilter, areaFilter, page, sort]);

     useEffect(() => {
          const timer = setTimeout(loadDealers, 300);

          return () => clearTimeout(timer);
     }, [loadDealers]);

     // A save changes what the list holds, so it is read again once the backend confirms. A row the
     // backend refused is reported in its own words rather than silently doing nothing.
     useEffect(() => {
          if (saveResult) {
               const failed = (saveResult.results || []).find(
                    (result) => !result.isSuccess,
               );

               setFailureMessage(failed ? failed.message : "");
               setPendingRetail({});

               actionDispatch(retailSalesClearSaveDealersResult());
               loadDealers();
          }
     }, [actionDispatch, saveResult, loadDealers]);

     const areaOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(Array.isArray(areas) ? areas : []).map((area) => ({
                    id: area.id,
                    name: area.area || area.name,
               })),
          ],
          [areas],
     );

     const retailAreaOptions = useMemo(
          () => areaOptions.filter((area) => area.id !== null),
          [areaOptions],
     );

     const saveDealer = useCallback(
          (dealer, isRetailCarSalesDealer, retailAreaId) => {
               actionDispatch(
                    retailSalesSaveDealersRequest({
                         dealers: [
                              {
                                   dealerCode: dealer.dealerCode,
                                   name: dealer.name,
                                   isRetailCarSalesDealer,
                                   retailAreaId: retailAreaId || undefined,
                              },
                         ],
                    }),
               );
          },
          [actionDispatch],
     );

     const rows = useMemo(
          () =>
               (dealerPage?.dealers || []).map((dealer) => ({
                    ...dealer,
                    isTicked:
                         dealer.isRetailCarSalesDealer ||
                         !!pendingRetail[dealer.id],
                    retailAreaOption: dealer.retailAreaId
                         ? { id: dealer.retailAreaId, name: dealer.retailArea }
                         : null,
               })),
          [dealerPage, pendingRetail],
     );

     const cells = useMemo(
          () => ({
               retailSwitch: (cellProps) => (
                    <td>
                         <input
                              type="checkbox"
                              className="k-checkbox k-checkbox-md"
                              checked={cellProps.dataItem.isTicked}
                              onChange={(event) => {
                                   const dealer = cellProps.dataItem;

                                   if (event.target.checked) {
                                        // Ticked on: wait for a retail area before saving.
                                        setFailureMessage("");
                                        setPendingRetail((previous) => ({
                                             ...previous,
                                             [dealer.id]: true,
                                        }));

                                        return;
                                   }

                                   setPendingRetail((previous) => {
                                        const next = { ...previous };
                                        delete next[dealer.id];

                                        return next;
                                   });

                                   // Only a dealer the backend already holds as retail needs saving.
                                   if (dealer.isRetailCarSalesDealer) {
                                        saveDealer(dealer, false);
                                   }
                              }}
                         />
                    </td>
               ),

               retailArea: (cellProps) => (
                    <td>
                         <DropDownList
                              style={{ width: "100%" }}
                              data={retailAreaOptions}
                              textField="name"
                              dataItemKey="id"
                              disabled={!cellProps.dataItem.isTicked}
                              value={cellProps.dataItem.retailAreaOption}
                              onChange={(event) =>
                                   // Picking the area is what saves the dealer as retail.
                                   saveDealer(
                                        cellProps.dataItem,
                                        true,
                                        event.value.id,
                                   )
                              }
                         />
                    </td>
               ),
          }),
          [retailAreaOptions, saveDealer],
     );

     const resetPaging = () => setPage(INITIAL_PAGE);

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "280px" }}
                         value={search}
                         onChange={(event) => {
                              setSearch(event.value);
                              resetPaging();
                         }}
                         placeholder="Search dealer name or code"
                    />
                    <DropDownList
                         style={{ width: "170px" }}
                         label="Retail"
                         data={RETAIL_OPTIONS}
                         textField="name"
                         dataItemKey="name"
                         value={retailFilter}
                         onChange={(event) => {
                              setRetailFilter(event.value);
                              resetPaging();
                         }}
                    />
                    <DropDownList
                         style={{ width: "170px" }}
                         label="Retail area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={areaFilter}
                         onChange={(event) => {
                              setAreaFilter(event.value);
                              resetPaging();
                         }}
                    />
                    <Button
                         type="button"
                         onClick={() => {
                              setSearch("");
                              setRetailFilter(RETAIL_OPTIONS[0]);
                              setAreaFilter(ALL_OPTION);
                              setSort([]);
                              resetPaging();
                         }}
                    >
                         Clear
                    </Button>

                    {/* Sits at the right-hand end of the filter row. */}
                    <Button
                         type="button"
                         themeColor="primary"
                         onClick={onAddDealer}
                         style={{ marginLeft: "auto" }}
                    >
                         Add a dealer
                    </Button>
               </div>

               {failureMessage && (
                    <p style={{ color: "#c62828", marginTop: "8px" }}>
                         {failureMessage}
                    </p>
               )}

               <div style={{ position: "relative" }}>
                    {isLoading && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}
                    <Grid
                         data={rows}
                         skip={page.skip}
                         take={page.take}
                         total={dealerPage?.totalCount || 0}
                         sort={sort}
                         sortable={true}
                         className="sba-area-data-grid"
                         pageable={{ buttonCount: 4, pageSizes: [10, 25, 50] }}
                         onPageChange={(event) => setPage(event.page)}
                         onSortChange={(event) => {
                              setSort(event.sort);
                              resetPaging();
                         }}
                    >
                         <GridColumn
                              field="dealerCode"
                              title="Dealer Code"
                              width="150px"
                         />
                         <GridColumn field="name" title="Dealer" width="300px" />
                         <GridColumn field="area" title="Area" width="110px" />
                         <GridColumn
                              field="isRetailCarSalesDealer"
                              title="Retail"
                              width="120px"
                              cells={{ data: cells.retailSwitch }}
                         />
                         <GridColumn
                              field="retailArea"
                              title="Retail Area"
                              width="180px"
                              cells={{ data: cells.retailArea }}
                         />
                    </Grid>
               </div>

          </div>
     );
};

export default RetailDealersGrid;
